/**
 * ledgerPlaceTransactions
 *
 * The placement rule of period close: a transaction whose natural `effectiveAt` falls in a closed
 * period P posts at the start instant of the first open period after P, carries `correctsPeriod`
 * = key(P), and keeps its snapshot, so an accrual keeps its natural `triggerAt`. Closed periods have
 * all ended, so that start is never in the future. Closes and reopens (reversals of a `close:` id)
 * are never placed. Returns new inputs and never mutates its own. Consumers: `ledgerPostTransactions`,
 * `ledgerAccrualWorker`, `ledgerAccrualBackfill` and `ledgerConfirmStatementImport`.
 */

import { DateTime } from 'luxon';
import type { LedgerCalendarStructure } from '../System.Ledger.Definition.types';
import type { LedgerTransactionInput, LedgerTransactionPlacement } from '../System.Ledger.types';
import type { LedgerPeriod } from './LedgerPeriod.types';
import { ledgerPeriodAt } from './ledgerPeriodAt';
import { ledgerPeriodKey } from './ledgerPeriodKey';
import { ledgerPeriodOf } from './ledgerPeriodOf';

function firstOpenPeriodAfter(index: number, closedPeriodKeys: ReadonlySet<string>, structure: LedgerCalendarStructure): LedgerPeriod {
    const next = ledgerPeriodAt(index + 1, structure);
    return closedPeriodKeys.has(ledgerPeriodKey(next)) ? firstOpenPeriodAfter(next.index, closedPeriodKeys, structure) : next;
}

function isCloseFamily(input: LedgerTransactionInput): boolean {
    return input.kind === 'periodClose' || (input.kind === 'reversal' && input.reversesTransactionId.startsWith('close:'));
}

function placementOf(
    input: LedgerTransactionInput,
    closedPeriodKeys: ReadonlySet<string>,
    structure: LedgerCalendarStructure,
): LedgerTransactionPlacement | null {
    if (isCloseFamily(input)) return null;
    const natural = DateTime.fromISO(input.effectiveAt, { setZone: true });
    if (!natural.isValid) return null;
    const period = ledgerPeriodOf(natural, structure);
    const periodKey = ledgerPeriodKey(period);
    if (!closedPeriodKeys.has(periodKey)) return null;
    return {
        transactionId: input.transactionId,
        requestedEffectiveAt: input.effectiveAt,
        effectiveAt: firstOpenPeriodAfter(period.index, closedPeriodKeys, structure).startAt.toJSDate().toISOString(),
        correctsPeriod: periodKey,
    };
}

function ledgerPlaceTransactions(
    transactions: readonly LedgerTransactionInput[],
    closedPeriodKeys: ReadonlySet<string>,
    structure: LedgerCalendarStructure,
): { transactions: LedgerTransactionInput[]; placements: LedgerTransactionPlacement[] } {
    const placed = transactions.map((input) => ({ input, placement: placementOf(input, closedPeriodKeys, structure) }));
    return {
        transactions: placed.map(({ input, placement }) => (placement === null
            ? input
            : { ...input, effectiveAt: placement.effectiveAt, correctsPeriod: placement.correctsPeriod })),
        placements: placed.flatMap(({ placement }) => (placement === null ? [] : [placement])),
    };
}

export { ledgerPlaceTransactions };
